/**
 * Copyright: ThoughtSpot Inc. 2019
 * Author: Thawsitt Naing (thawsitt.naing@thoughtspot.com)
 *
 * @fileoverview Utility functions for calculating tooltip position
 */
/* eslint-disable no-param-reassign */

import _ from 'lodash';
import { TOOLTIP_SPACING_PX, TooltipAlignment, TooltipPosition } from './tooltip-constants';

/**
 * Given an event, find out if the element has the "data-tooltip-content" attribute
 * and return its string value.
 *
 * @param {Event} event
 *
 * @return
 * targetEl {HTMLElement} - target element that triggered tooltip
 * tooltipText {string} - string value of the tooltip text or empty string
 */
export function getTooltipContent(
  event: Event
): { targetEl: HTMLElement; tooltipText: string } | undefined {
  let targetEl: any = event.target;
  if (!targetEl || !targetEl.getAttribute) {
    return undefined;
  }
  const isInsideShadowRoot = !!targetEl.shadowRoot;
  /**
   * If the target element is a shadow root HOST,
   * we get the element INSIDE the shadow root
   * which has data-tooltip-content attribute.
   */
  if (isInsideShadowRoot) {
    targetEl = event.composedPath()[0];
    if (!targetEl || !targetEl.getAttribute) {
      return undefined;
    }
  }
  let tooltipText: string = targetEl.getAttribute('data-tooltip-content');
  /**
   * If the target element does not have 'data-tooltip-content',
   * check the closest ancestor which has that attribute.
   * This is necessary for buttons with svg icons. e.g.
   *
   * <button data-tooltip-content="text">
   *     <svg ...>
   * <button>
   */
  if (!tooltipText) {
    targetEl = targetEl.closest('[data-tooltip-content]');
    tooltipText = targetEl && targetEl.getAttribute('data-tooltip-content');
  }
  return { targetEl, tooltipText };
}

/**
 * Return the top and left positions of the element relative to document.
 *
 * @param {HTMLElement} element - HTML element
 * @return {targetTop: number, targetLeft: number}
 */
export function getElementPosition(element: HTMLElement): {
  targetTop: number;
  targetLeft: number;
} {
  const rect = element.getBoundingClientRect();
  const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  return {
    targetTop: rect.top + scrollTop,
    targetLeft: rect.left + scrollLeft,
  };
}

/**
 * Return the tooltip position from 'data-tooltip-position' attribute.
 * If it is not provided or not valid, return TooltipPosition.top.
 *
 * @param {HTMLElement} targetEl - target element that triggered tooltip
 * @return {TooltipPosition} - TooltipPosition enum value
 */
export function getPositionAttribute(targetEl: HTMLElement): TooltipPosition {
  const position: string | null = targetEl.getAttribute('data-tooltip-position');
  return _.includes(Object.values(TooltipPosition), position)
    ? TooltipPosition[position!]
    : TooltipPosition.top;
}

/**
 * Return the tooltip alignment from 'data-tooltip-align' attribute.
 * If it is not provided or not valid, return TooltipAlignment.center.
 *
 * @param {HTMLElement} targetEl - target element that triggered tooltip
 * @return {TooltipAlignment} - TooltipAlignment enum value
 */
export function getAlignmentAttribute(targetEl: HTMLElement): TooltipAlignment {
  const align: string | null = targetEl.getAttribute('data-tooltip-align');
  return _.includes(Object.values(TooltipAlignment), align)
    ? TooltipAlignment[align!]
    : TooltipAlignment.center;
}

/**
 * Returns the tooltip max width from 'data-tooltip-max-width' attribute.
 * If it is not provided or not valid, returns null.
 *
 * @param {HTMLElement} targetEl - target element that triggered tooltip
 * @return {string | null} - max-width value
 */
export function getMaxWidthAttribute(targetEl: HTMLElement): string | null {
  const maxwidth: string | null = targetEl.getAttribute('data-tooltip-max-width');
  return maxwidth;
}

/**
 * Calculate the correct tooltip position and return its top, left values
 *
 * @param {HTMLElement} targetEl - target element that triggered tooltip
 * @param {HTMLDivElement} tooltipContainer - div element that contains tooltip text
 * @param {TooltipPosition} position - tooltip position
 * @param {TooltipAlignment} align - tooltip alignment
 * @param {boolean} shouldCheckEdgeCases - to check edge cases or not
 *
 * @return top and left coordinates of the tooltip container
 */
export function calculateTooltipPosition(
  targetEl: HTMLElement,
  tooltipContainer: HTMLDivElement,
  position: TooltipPosition,
  align: TooltipAlignment,
  shouldCheckEdgeCases: boolean
): { tooltipTop: number; tooltipLeft: number } {
  /**
   * Reset the tooltip container position here before
   * calculating its correct position. Otherwise, the previous
   * values can interfere with the calculations.
   */
  tooltipContainer.style.top = '0';
  tooltipContainer.style.left = '0';
  const { targetTop, targetLeft } = getElementPosition(targetEl);
  const targetWidth = targetEl.offsetWidth;
  const targetHeight = targetEl.offsetHeight;
  const tooltipWidth = tooltipContainer.offsetWidth;
  const tooltipHeight = tooltipContainer.offsetHeight;
  const centerEl = targetLeft + targetWidth / 2;

  let tooltipTop = 0;
  let tooltipLeft = 0;

  switch (position) {
    case TooltipPosition.top: {
      tooltipTop = targetTop - tooltipHeight - TOOLTIP_SPACING_PX;
      tooltipLeft = centerEl - tooltipWidth / 2;
      break;
    }
    case TooltipPosition.bottom: {
      tooltipTop = targetTop + targetHeight + TOOLTIP_SPACING_PX;
      tooltipLeft = centerEl - tooltipWidth / 2;
      break;
    }
    case TooltipPosition.left: {
      tooltipTop = targetTop + targetHeight / 2 - tooltipHeight / 2;
      tooltipLeft = targetLeft - tooltipWidth - TOOLTIP_SPACING_PX;
      break;
    }
    case TooltipPosition.right: {
      tooltipTop = targetTop + targetHeight / 2 - tooltipHeight / 2;
      tooltipLeft = targetLeft + targetWidth + TOOLTIP_SPACING_PX;
      break;
    }
    default:
      break;
  }

  if (position === TooltipPosition.top || position === TooltipPosition.bottom) {
    /**
     * Handles align-left and align-right
     */
    if (align === TooltipAlignment.left) {
      tooltipLeft = targetLeft;
    } else if (align === TooltipAlignment.right) {
      tooltipLeft = targetLeft + targetWidth - tooltipWidth;
    }
  }

  if (shouldCheckEdgeCases) {
    /**
     * Handles 4 edge cases where the tooltip overflows the viewport
     */
    const isTopOverflow = tooltipTop < 0;
    const isBottomOverflow = tooltipTop + tooltipHeight > window.innerHeight;
    const isLeftOverflow = tooltipLeft < 0;
    const isRightOverflow = tooltipLeft + tooltipWidth > window.innerWidth;

    let newPosition: TooltipPosition = position;
    let newAlign: TooltipAlignment = align;

    if (isTopOverflow && position === TooltipPosition.top) {
      newPosition = TooltipPosition.bottom;
    } else if (isBottomOverflow && position === TooltipPosition.bottom) {
      newPosition = TooltipPosition.top;
    }

    if (isLeftOverflow) {
      if (position === TooltipPosition.left) {
        newPosition = TooltipPosition.right;
      } else {
        newAlign = TooltipAlignment.left;
      }
    } else if (isRightOverflow) {
      if (position === TooltipPosition.right) {
        newPosition = TooltipPosition.left;
      } else {
        newAlign = TooltipAlignment.right;
      }
    }
    /**
     * Set shouldCheckEdgeCases: false to avoid infinite loops
     */
    if (newPosition !== position || newAlign !== align) {
      return calculateTooltipPosition(targetEl, tooltipContainer, newPosition, newAlign, false);
    }
  }
  tooltipContainer.className += ` rd-tooltip--position-${position} rd-tooltip--align-${align}`;
  return { tooltipTop, tooltipLeft };
}
